import styled from 'styled-components';
import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { TenantTypes } from 'TenantModule/constants';
import { CohortSelector } from 'TenantModule/components/Management/CohortSelector';
import { CsmSelector } from 'CsmModule/components/CsmSelector';
import { useCreateTenantFormFields } from 'TenantModule/hooks/useCreateTenantFormFields';
import { TagsListSectionWithInput } from 'components/TagsListSectionWithInput';
import { Divider } from 'components/material-ui-styles';
import { StyledDefaultPrimaryButton, StyledSimpleCard } from 'styles';

const TagContainer = styled.div`
  display: flex;
  .add-tag-div {
    flex: 2;
    min-width: 80px;
  }
  .initial-tag-div {
    flex: 2;
  }
  .start-date {
    flex: 2;
  }
`;

export const CreateTenantCard = () => {
  const {
    addTag,
    inputRef,
    tagsList,
    tagError,
    onCreateTenantFormKeyDown,
    onSubmitCreateTenantForm,
    onCreateTenantFormChange,
    createTenantParams,
    isRequesting,
    selectedCsm,
    handleCsmSelectorClick,
    onDeleteTag,
    tenantType,
    setTenantType,
    cohortId,
    setCohortId,
    isEnterprise,
    cohorts,
    isLoadingCohorts,
    isCohortsError,
    isSubmitBlocked,
    isInfraRequestEnabled,
    infraServiceOptions,
    infraServices,
    setInfraServices,
    vanityDomain,
    setVanityDomain,
    isTenantNameInvalidForInfra,
  } = useCreateTenantFormFields();

  return (
    <StyledSimpleCard>
      <h3>Create Tenant</h3>
      <form onSubmit={onSubmitCreateTenantForm} onKeyDown={onCreateTenantFormKeyDown}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <TextField
            required
            onChange={onCreateTenantFormChange}
            value={createTenantParams.tenantName}
            label="Tenant Name"
            name="tenantName"
            type="text"
            variant="outlined"
            disabled={isRequesting}
            // RDM-2277: only when tenant stack PRs are armed does the name feed
            // infrastructure identifiers; the server enforces the same rule.
            error={isTenantNameInvalidForInfra}
            helperText={
              isInfraRequestEnabled
                ? 'Lowercase letters, digits and single hyphens, starting with a letter (e.g. acme or acme-co). The name becomes the Kubernetes namespace, DNS zone and IAM role prefix.'
                : undefined
            }
          />

          <FormControl component="fieldset" disabled={isRequesting}>
            <FormLabel component="legend">Tenant type</FormLabel>
            {/* Radio (not Select) so the cluster picker appearing/disappearing is
                visually obvious. Shared is the pre-RDM-2031 default. */}
            <RadioGroup
              row
              name="tenantType"
              value={tenantType}
              onChange={(e) => setTenantType(e.target.value as TenantTypes)}
            >
              <FormControlLabel value={TenantTypes.Shared} control={<Radio />} label="Shared" />
              <FormControlLabel
                value={TenantTypes.Single}
                control={<Radio />}
                label="Single (dedicated cluster)"
              />
              <FormControlLabel
                value={TenantTypes.Enterprise}
                control={<Radio />}
                label="Enterprise (group filling)"
              />
            </RadioGroup>
          </FormControl>

          {isEnterprise ? (
            // The cohort replaces the manual secret: its applicationClusterSecret
            // is the source of truth for the cluster (the server rejects an
            // Enterprise body that supplies a secret).
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'warning.main',
                }}
              >
                Do not change the default selected cohort unless there are special instructions from
                the infrastructure team.
              </Typography>
              <CohortSelector
                cohorts={cohorts}
                isLoading={isLoadingCohorts}
                isError={isCohortsError}
                value={cohortId}
                onChange={setCohortId}
                disabled={isRequesting}
              />
            </Box>
          ) : (
            <TextField
              required
              onChange={onCreateTenantFormChange}
              value={createTenantParams.secretName}
              label="Secret Name"
              name="secretName"
              type="text"
              variant="outlined"
              disabled={isRequesting}
            />
          )}
          {isInfraRequestEnabled && infraServiceOptions.length > 0 && (
            <Autocomplete
              multiple
              options={infraServiceOptions}
              value={infraServices}
              onChange={(_event, next) => setInfraServices(next)}
              disabled={isRequesting}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Connector agents (optional)"
                  helperText="Connector agents the tenant stack PR deploys for this tenant."
                />
              )}
            />
          )}
          {isInfraRequestEnabled && (
            <TextField
              onChange={(e) => setVanityDomain(e.target.value)}
              value={vanityDomain}
              label="Vanity domain (optional)"
              name="vanityDomain"
              type="text"
              variant="outlined"
              disabled={isRequesting}
              helperText="A vanity domain requires an infrastructure engineer's first apply"
            />
          )}
          <CsmSelector
            accountId={selectedCsm?.accountId || null}
            onClick={handleCsmSelectorClick}
            tooltip="Select Customer Success Manager for this team"
            disabled={isRequesting}
          />
        </Box>

        <FormControl component="fieldset" fullWidth>
          <Box component="h3">Add tags to this tenant</Box>
          <TagContainer>
            <div>
              <TagsListSectionWithInput
                tagList={tagsList}
                onDelete={onDeleteTag}
                inputRef={inputRef}
                onKeyDown={addTag}
                helperText={tagError}
                error={!!tagError}
                disabled={isRequesting}
                placeholder="Enter custom tag"
              />
            </div>
          </TagContainer>
        </FormControl>
        <Divider sx={{ my: 6 }} />
        <StyledDefaultPrimaryButton type="submit" disabled={isRequesting || isSubmitBlocked}>
          Submit
        </StyledDefaultPrimaryButton>
      </form>
    </StyledSimpleCard>
  );
};
